/**
 * Governance posture: policy compliance and Defender secure score per
 * subscription, with day-over-day movement from the backend's history tables.
 *
 * Deltas compare today against `delta_baseline_day`. When no history exists
 * yet the backend sends null — and null renders as "—", never as zero,
 * because unknown movement is not the same as no movement.
 */

import { ArrowDown, ArrowUp, Icon, Minus } from "@norican/icons";
import { DataGrid, EmptyState, Statistic, Typography } from "@norican/react";

import type { CloudGovernance, SubscriptionGovernance } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatCount, formatDay, formatPercent } from "./status";

interface GovernancePanelProps {
  governance: CloudGovernance;
}

/**
 * A day-over-day movement with a direction arrow.
 *
 * `goodWhenUp` maps direction onto meaning: a rising secure score is good, a
 * rising non-compliant count is not. Null means "no baseline yet", rendered
 * as an em dash — unknown, not zero.
 */
function Delta({
  value,
  unit = "",
  goodWhenUp,
}: {
  value: number | null;
  unit?: string;
  goodWhenUp: boolean;
}) {
  if (value == null) {
    return <span className="text-fg-subtle text-xs">—</span>;
  }
  if (value === 0) {
    return (
      <span className="text-fg-muted inline-flex items-center gap-0.5 text-xs tabular-nums">
        <Icon icon={Minus} size="xs" />0{unit}
      </span>
    );
  }
  const improved = value > 0 === goodWhenUp;
  const magnitude = Math.abs(value);
  const formatted = Number.isInteger(magnitude) ? magnitude.toLocaleString() : magnitude.toFixed(1);
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs tabular-nums ${
        improved ? "text-success-fg" : "text-danger-fg"
      }`}
    >
      <Icon icon={value > 0 ? ArrowUp : ArrowDown} size="xs" />
      {formatted}
      {unit}
    </span>
  );
}

export function GovernancePanel({ governance }: GovernancePanelProps) {
  if (!governance.configured) {
    return (
      <PanelCard title="Governance" caption="Not available">
        <Typography variant="body-sm" tone="muted">
          {governance.status_detail}
        </Typography>
      </PanelCard>
    );
  }

  const evaluated = governance.compliant + governance.non_compliant;
  const estatePct = evaluated > 0 ? (governance.compliant / evaluated) * 100 : null;

  return (
    <PanelCard
      title="Governance"
      caption={
        governance.delta_baseline_day
          ? `Movement vs ${formatDay(governance.delta_baseline_day)}`
          : "No history yet — day-over-day movement unknown"
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Statistic
            label="Policy compliance"
            value={formatPercent(estatePct)}
            tone={estatePct != null && estatePct < 90 ? "warning" : "success"}
            size="lg"
            caption={`${formatCount(evaluated)} policy states evaluated`}
          />
          <Statistic
            label="Non-compliant states"
            value={formatCount(governance.non_compliant)}
            tone={governance.non_compliant > 0 ? "warning" : "success"}
            delta={<Delta value={governance.non_compliant_delta} goodWhenUp={false} />}
          />
          <Statistic
            label="Compliant states"
            value={formatCount(governance.compliant)}
            tone="neutral"
          />
          <Statistic
            label="Secure score coverage"
            value={`${formatCount(governance.subscriptions_with_secure_score)} / ${formatCount(
              governance.subscriptions.length,
            )}`}
            caption="Subscriptions Defender scores"
          />
        </div>

        {governance.subscriptions.length === 0 ? (
          <EmptyState
            size="sm"
            title="No governance data collected yet"
            description={governance.status_detail}
          />
        ) : (
          <DataGrid<SubscriptionGovernance>
            data={governance.subscriptions}
            getRowId={(subscription) => subscription.subscription_id}
            density="compact"
            aria-label="Governance posture per subscription, worst compliance first"
            columns={[
              {
                id: "name",
                header: "Subscription",
                accessor: (subscription) => subscription.name,
                sortable: true,
                cell: (subscription) => (
                  <span className="text-fg font-medium">{subscription.name}</span>
                ),
              },
              {
                id: "compliance",
                header: "Policy compliance",
                accessor: (subscription) => subscription.compliance_pct,
                sortable: true,
                align: "end",
                cell: (subscription) => (
                  <span className="inline-flex items-center gap-2">
                    <Delta value={subscription.compliance_pct_delta} unit=" pt" goodWhenUp />
                    <span className="text-fg tabular-nums">
                      {formatPercent(subscription.compliance_pct)}
                    </span>
                  </span>
                ),
              },
              {
                id: "non-compliant",
                header: "Non-compliant",
                accessor: (subscription) => subscription.non_compliant,
                sortable: true,
                align: "end",
                cell: (subscription) => (
                  <span className="inline-flex items-center gap-2">
                    <Delta value={subscription.non_compliant_delta} goodWhenUp={false} />
                    <span className="text-fg tabular-nums">
                      {formatCount(subscription.non_compliant)}
                    </span>
                  </span>
                ),
              },
              {
                id: "secure-score",
                header: "Secure score",
                accessor: (subscription) => subscription.secure_score_pct,
                sortable: true,
                align: "end",
                cell: (subscription) => (
                  <span className="inline-flex items-center gap-2">
                    <Delta value={subscription.secure_score_delta} unit=" pt" goodWhenUp />
                    <span className="text-fg tabular-nums">
                      {formatPercent(subscription.secure_score_pct)}
                    </span>
                  </span>
                ),
              },
            ]}
          />
        )}

        {governance.errors.length > 0 && (
          <Typography variant="body-sm" tone="muted">
            Collection problems: {governance.errors.join(" · ")}
          </Typography>
        )}
      </div>
    </PanelCard>
  );
}
